import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';

import { Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { MessageService } from 'src/app/shared/services/message.service';
import { Farm } from 'src/app/shared/dtos/Farm';
import { FarmWithImages } from 'src/app/shared/dtos/FarmWithImages';
import { environment } from 'src/environments/environment';
import { FarmDTOGeneric } from 'src/app/shared/dtos/FarmDTOGeneric';
import { portugal } from 'src/app/shared/constants/portugal';
import { provinces } from 'src/app/shared/constants/provinces';
import { countries } from 'src/app/shared/constants/countries';
import { FarmFilters } from 'src/app/shared/dtos/FarmFilters';
import { Province } from 'src/app/shared/dtos/Province';

@Injectable({
  providedIn: 'root'
})
export class FarmService {

  private env = environment;
  private serverUrl = environment.serverUrl + 'farms';  // URL to web api
  private portugal = portugal;
  private provinces = provinces;
  private countries = countries;

  httpOptions = {
    headers: new HttpHeaders({ 'Content-Type': 'application/json' })
  };

  constructor(
    private http: HttpClient,
    private messageService: MessageService) { }

    /** GET farms from the server */
  getFarms (): Observable<Farm[]> {
    return this.http.get<Farm[]>(this.serverUrl)
      .pipe(
        tap(_ => this.log('fetched farms')),
        catchError(this.handleError<Farm[]>('getFarms', []))
      );
  }

  getFarmsCurrentUser(): Observable< Farm []> {
    return this.http.get<Farm[]>(this.serverUrl + '/currentUser')
      .pipe(
        tap(_ => this.log('fetched farms')),
        catchError(this.handleError<Farm[]>('getFarms', []))
      );
  }

  /** GET farm by id. Will 404 if id not found */
  getFarmById(id: number): Observable<FarmDTOGeneric | undefined> {
    const url = `${this.serverUrl}/${id}`;
    return this.http.get<FarmDTOGeneric>(url).pipe(
      tap(_ => this.log(`fetched farm id=${id}`)),
      catchError(this.handleError<FarmDTOGeneric>(`getFarm id=${id}`))
    );
  }

  filterFarms(data: any): Observable<Farm[]> {
    return this.http.get<Farm[]>(this.serverUrl + '/search', {
      params: data
    });
  }

  /* GET farms whose name contains search term */
  //CAMBIAR PARA ENVIAR UN FORM (farm) CON VARIOS CAMPOS
   searchFarms(term: string): Observable<Farm[]> {
    if (!term.trim()) {
      // if not search term, return empty farm array.
      return of([]);
    }
    return this.http.get<Farm[]>(`${this.serverUrl}/?name=${term}`).pipe(
      tap(x => x.length ?
         this.log(`found farms matching "${term}"`) :
         this.log(`no farms matching "${term}"`)),
      catchError(this.handleError<Farm[]>('searchFarms', []))
    );
  }

  //////// Save methods //////////

   /** POST: add a new farm to the server */
   addFarmSpain (spainFarmDTO: FarmWithImages): Observable<Farm> {
    return this.http.post<Farm>(this.serverUrl + '/spain', spainFarmDTO, this.httpOptions).pipe(
      tap((newFarm: Farm) => this.log(`added farm w/ id=${newFarm.id}`)),
      catchError(this.handleError<Farm>('addFarm'))
    );
  }

  addFarmPortugal (portugalFarmDTO: FarmWithImages): Observable<Farm> {
    return this.http.post<Farm>(this.serverUrl + '/portugal', portugalFarmDTO, this.httpOptions).pipe(
      tap((newFarm: Farm) => this.log(`added farm w/ id=${newFarm.id}`)),
      catchError(this.handleError<Farm>('addFarm'))
    );
  }

  /** DELETE: delete the farm from the server */
  deleteFarm (farm: Farm | number): Observable<Farm> {
    const id = typeof farm === 'number' ? farm : farm.id;
    const url = `${this.serverUrl}/${id}`;

    return this.http.delete<Farm>(url, this.httpOptions).pipe(
      tap(_ => this.log(`deleted farm id=${id}`)),
      catchError(this.handleError<Farm>('deleteFarm'))
    );
  }

  /** PUT: update the farm on the server */
  updateFarmSpain (farm: Farm): Observable<any> {
    return this.http.put<Farm>(this.serverUrl + '/spain', farm, this.httpOptions).pipe(
      tap(_ => this.log(`updated farm id=${farm.id}`)),
      catchError(this.handleError<any>('updateFarm'))
    );
  }

  updateFarmPortugal (farm: Farm): Observable<any> {
    return this.http.put<Farm>(this.serverUrl + '/portugal', farm, this.httpOptions).pipe(
      tap(_ => this.log(`updated farm id=${farm.id}`)),
      catchError(this.handleError<any>('updateFarm'))
    );
  }

  /** GET: full url to view the photo */
  getUrlImage(url: string) {
    return this.env.urlImages.concat(url);
  }

  getDistrictsPortugal() {
    return portugal.filter(port => port.level === 1 );
  }

  getCountries() {
    return countries;
  }
  /**
   * Handle Http operation that failed.
   * Let the app continue.
   * @param operation - name of the operation that failed
   * @param result - optional value to return as the observable result
   */
  private handleError<T> (operation = 'operation', result?: T) {
    return (error: any): Observable<T> => {

      // TODO: send the error to remote logging infrastructure
      console.error(error); // log to console instead

      // TODO: better job of transforming error for user consumption
      this.log(`${operation} failed: ${error.message}`);

      // Let the app keep running by returning an empty result.
      return of(result as T);
    };
  }


   /** Log a FarmService message with the MessageService */
   private log(message: string) {
    this.messageService.add(`Farm Service: ${message}`);
  }
}
